import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import ExpandableContainer from "@/shared/ui/Expandable";
import { SidebarLink } from "./SidebarLink";
import { useNavigationContainer } from "@/shared/contexts/navigation.context";
import { cn } from "@/shared/utils/shadcn";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { Column, Row } from "@/shared/ui/Blocks";
import { useViewer } from "@/features/workspace/hooks/useViewer";
import { useLogout } from "@/features/workspace/hooks/useLogout";
import useNotifications from "@/features/profile/hooks/useNotifications";
import NotificationButton from "@/shared/ui/NotificationButton";

interface SidebarProps {
  heightOffset?: number;
}

function Sidebar(props: SidebarProps) {
  const { heightOffset = 0 } = props;
  const { viewer } = useViewer();
  const [foceOpen, setForceOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    sidebarLinks,
    dashboardLink,
    mobileOpen,
    shouldUseMobileUI,
    closeMobileSidebar,
  } = useNavigationContainer();
  const { handleLogout } = useLogout();
  const {
    notifications,
    notificationCount,
    loading: loadingNotifications,
  } = useNotifications();

  const isForcedOpen = foceOpen || mobileOpen;

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--content-above-height",
      `${heightOffset}px`
    );
  }, [heightOffset]);

  const SidebarNavLinks = useCallback(
    ({ isExpanded }: { isExpanded?: boolean }) => (
      <div className="flex flex-col flex-1 space-y-2 px-2 mt-2">
        {sidebarLinks.map(({ key, ...rest }) => (
          <SidebarLink key={key} isExpanded={isExpanded} {...rest} />
        ))}
      </div>
    ),
    [sidebarLinks]
  );

  const DashboardSection = useCallback(
    ({ isExpanded }: { isExpanded?: boolean }) => (
      <div className="mt-4 px-2">
        <SidebarLink
          id="sidebar-link-dashboard"
          isExpanded={isExpanded}
          {...dashboardLink}
          key="dashboard"
        />
      </div>
    ),
    [dashboardLink]
  );

  const SectionTitle = useCallback(
    ({ title }: { title: string }) => (
      <p className="text-gray-500 text-nowrap font-semibold text-ellipsis uppercase text-2xs ml-4 tracking-[1px]">
        {title}
      </p>
    ),
    []
  );

  const UserProfileSection = useCallback(
    ({ isExpanded }: { isExpanded: boolean }) => (
      <AnimatePresence>
        {isExpanded && (
          <Column className="border-solid border-b border-grey-300 gap-2 p-2">
            <SectionTitle title={t("sidebar.my_profile_section")} />
            <SidebarLink
              id="sidebar-link-my-profile"
              key="my-profile"
              label={t("user.my_profile")}
              isExpanded={isExpanded}
              icon="UserProfile"
              url="/profile"
            />
            <SidebarLink
              id="sidebar-link-profile-settings"
              key="profile-settings"
              label={t("profile_settings.title")}
              isExpanded={isExpanded}
              icon="Gear"
              url="/profile/settings"
            />
            <SidebarLink
              id="sidebar-link-log-out"
              key="log-out"
              label={t("sidebar.log_out")}
              isExpanded={isExpanded}
              icon="Logout"
              onClick={handleLogout}
            />
          </Column>
        )}
      </AnimatePresence>
    ),
    [t, handleLogout]
  );

  const SidebarBottomSection = ({ isExpanded }: { isExpanded?: boolean }) => (
    <div className="flex flex-col flex-colspace-y-2 py-4 border-solid border-b border-grey-300 gap-2">
      <UserProfileSection isExpanded={!!isExpanded} />
      <Column className="px-2 gap-2 w-full">
        <SidebarLink
          key="support"
          label={t("sidebar.support")}
          isExpanded={isExpanded}
          icon="Support"
          id="sidebar-link-support"
          url="https://support.assessio.ai"
        />
        <NotificationButton
          notifications={notifications}
          notificationCount={notificationCount}
          loading={loadingNotifications}
          showLabel={isExpanded}
          align="end"
          side="right"
          sideOffset={16}
        />
        {/* <SidebarLink
          key="notifications"
          label="Notifications"
          isExpanded={isExpanded}
          icon="Bell"
        /> */}
      </Column>
      <Row
        className={cn(
          "px-4 pt-4 border-t border-grey-300 border-solid cursor-pointer"
        )}
        onClick={() => {
          navigate("/profile");
          if (shouldUseMobileUI) {
            closeMobileSidebar();
          }
        }}
        id="sidebar-link-profile"
      >
        <AvatarWithInitials
          avatar={viewer?.avatar_url || ""}
          initials={viewer?.initials || ""}
          size="sm"
        />
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              layout
              key="sidebar-avatar-name-container"
              className="flex flex-col ml-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, dur: 0.1 }}
              transition={{
                opacity: { ease: "linear" },
                layout: { duration: 0.3 },
              }}
            >
              <span className="text-xs leading-tight font-semibold text-gray-700 text-nowrap text-ellipsis">
                {viewer?.name}
              </span>
              <span className="text-xs leading-tight text-gray-500">
                {viewer?.email}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </Row>
    </div>
  );

  // On touch devices/mobile UI, sidebar is hidden unless explicitly opened via burger menu
  // On desktop (non-touch, large screen), sidebar is always visible with hover expand
  const showSidebar = shouldUseMobileUI ? mobileOpen : true;

  return (
    <div
      className={cn(
        "sticky z-50 top-(--content-above-height) h-[calc(100vh-var(--content-above-height))] w-[64px] bg-blue-200",
        !showSidebar && "hidden"
      )}
    >
      <ExpandableContainer
        className={cn(
          "flex flex-col bg-white z-10 shadow-md h-full overflow-hidden"
        )}
        keepExpanded={isForcedOpen}
        expandFull={mobileOpen}
      >
        <DashboardSection />
        <SidebarNavLinks />
        <SidebarBottomSection />
      </ExpandableContainer>
    </div>
  );
}

export { Sidebar };
